import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import i18n from '@/i18n';

const STORAGE_KEY = 'rxk-language';

function detectLanguage(): 'en' | 'ko' | 'bn' {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'ko' || saved === 'bn' || saved === 'en') return saved;
  } catch { /* ignore */ }

  return 'en';
}

export function useLanguageInit() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const lang = detectLanguage();
    i18n.changeLanguage(lang);
    document.documentElement.lang = lang;

    // Sync URL with language on first load
    const path = location.pathname;
    const isKoPath = path.startsWith('/ko');
    const isBnPath = path.startsWith('/bn');
    const cleanPath = (isKoPath || isBnPath) ? path.replace(/^\/(ko|bn)/, '') || '/' : path;

    if (lang === 'ko' && !isKoPath) {
      navigate('/ko' + cleanPath, { replace: true });
    } else if (lang === 'bn' && !isBnPath) {
      navigate('/bn' + cleanPath, { replace: true });
    } else if (lang === 'en' && (isKoPath || isBnPath)) {
      navigate(cleanPath, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export function useLanguageSwitch() {
  const navigate = useNavigate();
  const location = useLocation();

  const switchLanguage = (lang: 'en' | 'ko' | 'bn') => {
    localStorage.setItem(STORAGE_KEY, lang);
    i18n.changeLanguage(lang);
    document.documentElement.lang = lang;

    const path = location.pathname;
    const isKoPath = path.startsWith('/ko');
    const isBnPath = path.startsWith('/bn');
    const cleanPath = (isKoPath || isBnPath) ? path.replace(/^\/(ko|bn)/, '') || '/' : path;

    if (lang === 'ko' && !isKoPath) {
      navigate('/ko' + cleanPath);
    } else if (lang === 'bn' && !isBnPath) {
      navigate('/bn' + cleanPath);
    } else if (lang === 'en' && (isKoPath || isBnPath)) {
      navigate(cleanPath);
    }
  };

  return { switchLanguage, currentLang: i18n.language as 'en' | 'ko' | 'bn' };
}

export function getLocalizedPath(path: string, lang: string): string {
  if (lang === 'ko') {
    if (path.startsWith('/ko')) return path;
    return '/ko' + path;
  }
  if (lang === 'bn') {
    if (path.startsWith('/bn')) return path;
    return '/bn' + path;
  }
  if (path.startsWith('/ko') || path.startsWith('/bn')) return path.replace(/^\/(ko|bn)/, '') || '/';
  return path;
}